// @givmo/board — the director wire as the director pages read it (CBG-SEC2 SEC2-WEB).
//
// The generated models are the contract since the one regeneration from landed `development`
// (SEC2-BE1b): `/me` (`OrgBoardDirectorMeOut`) with its boards, signing window (law 8) and intent
// statements (§8.3), and the claim's answers (§3.2). What this file adds is the shape the TOLERANT
// decodes hand on (`director-me.ts`, the invitation page): each generated model with every field it
// marks optional filled in with what its absence means, and every enum widened to `string`, because an
// unknown value is kept and shown, never swallowed. A field the backend adds to a model reaches these
// shapes with the next regeneration.

import {
  firstErrorCode,
  isGivmoError,
  type BoardDirectorSigningOut,
  type BoardIntentStatementOut,
  type BoardInvitationPreviewOut,
  type BoardSeatClaimCodeOut,
  type OrgBoardDirectorBoardOut,
} from '@givmo/api-client';

/** The five signature-class acts, by the name the intent registry keys them on (contract §8.3). */
export type { BoardSigningAct } from '@givmo/api-client';

/** A string enum, as a tolerant decode keeps it: any string. */
type Widened<Value> = Value extends string ? string : Value;

/** A generated model as a tolerant decode hands it on: no field missing, every enum a string. */
type Decoded<Model> = { [Field in keyof Model]-?: Widened<Exclude<Model[Field], undefined>> };

/**
 * This sign-in's signing window, computed server-side (law 8). `closes_at` is fixed when it is open;
 * `account_has_passkey` says which confirmation can open it when it is shut (rule 3), and is null when
 * the account's factors can't be read — a window no confirmation can open.
 */
export type DirectorSigning = Decoded<BoardDirectorSigningOut>;

/** The words one act affirms beside its button; the act echoes `id` (contract §8.3). */
export type DirectorIntentStatement = Decoded<BoardIntentStatementOut>;

/**
 * One board this account holds a claimed seat on: a directory entry, never a grant (contract §6).
 * The retired `access_mode` and `step_up_required_for_all_access` (D-CBG-20) left the wire with
 * SEC2-BE2; an older backend's copy of them is never read.
 */
export type DirectorBoard = Decoded<OrgBoardDirectorBoardOut>;

/**
 * A seat for someone who isn't on the board (CBG-YR6-OUTSIDE): its scope is its committees, and it
 * opens only their meetings, packets, minutes and written consents. The decode reads any other value,
 * or none, as the board's.
 */
export function isCommitteeOnly(board: Pick<DirectorBoard, 'scope'> | null | undefined): boolean {
  return board?.scope === 'committees';
}

/** The signed-in account's own view as a director (contract §6). */
export interface DirectorMe {
  boards: DirectorBoard[];
  signing: DirectorSigning;
  intent_statements: DirectorIntentStatement[];
}

/** What an invitation is for, before it is used (contract §3.2). Nothing is written to answer it. */
export type BoardInvitationPreview = Decoded<BoardInvitationPreviewOut>;

/** A claim code's answer: whether it was mailed, where (masked), and until when (contract §3.3). */
export type BoardSeatClaimCode = Decoded<BoardSeatClaimCodeOut>;

/** The typed refusals the director door answers with (contract §12.2). */
export const DIRECTOR_REFUSALS = {
  signInRequired: 'board_director_sign_in_required',
  accessDenied: 'board_director_access_denied',
  signingRequired: 'board_director_signing_required',
  intentChanged: 'board_intent_statement_changed',
  invitationUnavailable: 'board_invitation_unavailable',
  claimCodeRequired: 'board_seat_claim_code_required',
  claimCodeFailed: 'board_seat_claim_code_failed',
  claimCodeThrottled: 'board_seat_claim_code_throttled',
  claimCodeNotSent: 'board_seat_claim_code_not_sent',
  alreadySeatedHere: 'board_account_already_seated_here',
  /**
   * CBG-SEC2-L4 (D-CBG-23): another organization's sign-in lock binds this account, so it can't
   * join this board or sign for it: the page offers another Givmo account (frame a1).
   */
  anotherAccountRequired: 'board_another_account_required',
  // CBG-YR3-ROLES layer 2: the title chosen as "Signing as" is no longer this seat's.
  capacityNotHeld: 'board_signature_capacity_not_held',
  /**
   * CBG-SEC2-L4-MOVE (D-CBG-23 flow (b)): the move's ticket can't be used — unknown, past its 30
   * minutes, spent, or its seat no longer the one it started on (frame b9c: nothing moved).
   */
  seatMoveUnavailable: 'board_seat_move_unavailable',
  /** CBG-SEC2-L4-MOVE: the account finishing the move is the one the seat is on (frame b9a). */
  seatMoveSameAccount: 'board_seat_move_same_account',
  /**
   * CBG-SEC5-MARK: Board couldn't mark the account on core before linking the seat, so nothing was
   * linked, and the same act, with the same code, can be sent again (a typed 503; a code typed is
   * charged one attempt and stays good). The join and the move stay where they were and say the
   * backend's own sentence (CBG-FIX161).
   */
  seatLinkUnavailable: 'board_seat_link_unavailable',
} as const;

/**
 * Approved (CBG-SEC5-MARK's two sentences, word for word the backend's; CBG-UX-COPY5, D-CBG-52):
 * what the join and the move say on `seatLinkUnavailable` when the answer carries no sentence of its
 * own. The page says the backend's sentence when it has one (CBG-FIX161).
 */
export const SEAT_LINK_UNAVAILABLE = {
  claim:
    'We couldn’t connect your Givmo account to this seat just now. Please try again in a moment.',
  move: 'We couldn’t move this seat to your Givmo account just now. Please try again in a moment.',
} as const;

/**
 * CBG-FIX161: what the join or the move says on `seatLinkUnavailable`: the backend's own sentence
 * when the answer carries one, else `SEAT_LINK_UNAVAILABLE`'s same words. Read here, beside the
 * code, so a director page reaches no officer module to read it (the capacity rail).
 */
export function seatLinkUnavailableSentence(error: unknown, act: 'claim' | 'move'): string {
  if (isGivmoError(error)) {
    const detail = error.entries[0]?.detail;
    if (typeof detail === 'string' && detail.trim().length > 0) return detail.trim();
  }
  return SEAT_LINK_UNAVAILABLE[act];
}

/**
 * What a director's page says when the board refuses this account (`accessDenied`): their seat was
 * recorded as former, or their account disconnected, while the page was open. The seat is re-read
 * on every request, so the next act or download meets it. Copy approved (copy round 3,
 * CBG-SEC2-COPY3, D-CBG-18). The backend's refusal carries the same sentence; the page says it from
 * the code, so it reads the same whichever backend answers.
 */
export const DIRECTOR_NO_LONGER_HAS_ACCESS = 'You no longer have access to this board.';

/**
 * CBG-FIX104 (row 4.1 and note 1, approved 2026-10-17 17:42 PDT): the same refusal to a seat this
 * account's own `/me` listed as a committee's, for someone who isn't on the board — who never had
 * access to the board. One committee, or several. The backend's sentence stays one for every cause
 * (no oracle), and no page shows it: each page says its own, from what `/me` listed.
 */
export const DIRECTOR_NO_LONGER_HAS_ACCESS_COMMITTEE =
  'You no longer have access to this committee.';
export const DIRECTOR_NO_LONGER_HAS_ACCESS_COMMITTEES =
  'You no longer have access to these committees.';

/** What `/me` last listed of this account's seat on a board: whose seat it was, and its committees. */
export interface ListedSeat {
  /** A seat for someone who isn't on the board (`isCommitteeOnly`). */
  committeeOnly: boolean;
  /** How many committees `/me` listed it on. */
  committees: number;
  /** Their names, as `/me` listed them: a page about the board names its one committee. */
  names: readonly string[];
}

/**
 * The access refusal's sentence for a seat as `/me` listed it: a committee seat's in the committee's
 * words ("these committees" for several, else "this committee"), and the board's line for a board
 * seat, or for a seat this page never saw listed. A seat on no committee, every membership ended,
 * takes the one committee's line, as Board's mails speak to it (CBG-FIX111, row 1.4).
 */
export function noLongerHasAccess(listed: ListedSeat | null): string {
  if (listed === null || !listed.committeeOnly) return DIRECTOR_NO_LONGER_HAS_ACCESS;
  return listed.committees > 1
    ? DIRECTOR_NO_LONGER_HAS_ACCESS_COMMITTEES
    : DIRECTOR_NO_LONGER_HAS_ACCESS_COMMITTEE;
}

/** Whether a failed director request is the board refusing this account (`accessDenied`). */
export function isAccessDenied(caught: unknown): boolean {
  return isGivmoError(caught) && firstErrorCode(caught) === DIRECTOR_REFUSALS.accessDenied;
}

/**
 * A failed director request's sentence, where a page shows the backend's own: the access refusal in
 * its approved words — for the seat `/me` listed on this page's board, `listed` (CBG-FIX104) — any
 * other refusal's own sentence, and `fallback` for a failure that isn't one.
 */
export function directorFailureSentence(
  caught: unknown,
  fallback: string,
  listed: ListedSeat | null = null,
): string {
  if (!isGivmoError(caught)) return fallback;
  return isAccessDenied(caught) ? noLongerHasAccess(listed) : caught.message;
}
